import { format, getTime, formatDistanceToNow } from 'date-fns';

// ----------------------------------------------------------------------

type InputValue = Date | string | number | null;

/**
 * A date of birth is a calendar date, not an instant: build it in local time
 * from its YYYY-MM-DD part so no timezone shifts it to the previous day.
 */
export function parseDateOnly(
  value: string | Date | null | undefined,
): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const m = String(value)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

export function fDate(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'dd MMM yyyy';
  if (!date) return '';

  let d: Date;
  if (typeof date === 'string') {
    const s = date.trim();
    const m = s.match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const da = Number(m[3]);
      d = new Date(y, mo, da);
    } else {
      d = new Date(s);
    }
  } else {
    d = new Date(date as any);
  }

  return isNaN(d.getTime()) ? '' : format(d, fm);
}

export function fDateTime(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'dd MMM yyyy p';

  return date ? format(new Date(date), fm) : '';
}

export function fTimestamp(date: InputValue) {
  return date ? getTime(new Date(date)) : '';
}

/** Clock time in the viewer's local zone, 24-hour "HH:mm" (e.g. 14:05). */
export function fTime(timestamp: any) {
  if (timestamp === null || timestamp === undefined || timestamp === '') {
    return '';
  }
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return '';
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Shared display formats. Time is always 12-hour with AM/PM — native
 * `<input type="time">` renders in the browser's locale and cannot be forced,
 * so use a MUI TimePicker with these wherever a time is entered.
 */
export const DATE_FORMAT = 'dd/MM/yyyy';
export const TIME_FORMAT = 'hh:mm a';

/**
 * Times are stored/transported as 24h "HH:mm" strings. These convert at the
 * edges so pickers can work with Date values without changing the wire format.
 */
export function parseClockTime(value: string | null | undefined): Date | null {
  if (!value) return null;
  const [rawHours, rawMinutes] = String(value).split(':');
  const hours = Number(rawHours);
  const minutes = Number(rawMinutes);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  const parsed = new Date();
  parsed.setHours(hours, minutes, 0, 0);
  return parsed;
}

export function serializeClockTime(value: Date | null): string | null {
  if (!value || isNaN(value.getTime())) return null;
  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function fToNow(date: InputValue) {
  return date
    ? formatDistanceToNow(new Date(date), {
        addSuffix: true,
      })
    : '';
}
